import {
  ExperimentBatchChannel,
  ExperimentBatchItem,
  ExperimentBatchQueue,
  ExperimentFlushTimer,
  ExperimentTrackingParams,
} from 'core/analytics/entities';
import { SECOND } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import { AppEnvironment, HttpClient, StorageInterface } from 'core/common/services';
import { ResponseHeaders } from 'core/common/services/httpClient/entities';
import { Logger } from 'core/common/services/logger/interfaces';
import { isValidUuid } from 'core/common/utils/string';
import { BackendAnalyticsService } from '../../interfaces';

export class HttpBackendAnalyticsService implements BackendAnalyticsService {
  private readonly batchSize = 5;
  private readonly batchTimeout = SECOND;

  private readonly queues: ExperimentBatchQueue = {
    [ExperimentBatchChannel.SPLIT_ID]: [],
    [ExperimentBatchChannel.USER_ID]: [],
  };

  private readonly flushTimers: ExperimentFlushTimer = {
    [ExperimentBatchChannel.SPLIT_ID]: null,
    [ExperimentBatchChannel.USER_ID]: null,
  };

  constructor(
    private readonly httpClient: HttpClient,
    private readonly logger: Logger,
    private readonly appEnvironment: AppEnvironment,
    private readonly storageService: StorageInterface,
  ) {}

  async setSplitExperimentProperties(params: ExperimentTrackingParams) {
    const { experimentName, groupName } = params;

    const headers = this.storageService.get<ResponseHeaders>('headers');

    if (!!headers) {
      await this.enqueueExperiment(ExperimentBatchChannel.USER_ID, {
        name: experimentName,
        group: groupName,
      });

      return;
    }

    await this.enqueueExperiment(ExperimentBatchChannel.SPLIT_ID, {
      name: experimentName,
      group: groupName,
    });
  }

  async trackEvent(eventName: string, data: Record<string, string>): Promise<void> {
    await this.httpClient.post('/analytics-event-service/events/client-event', {
      data: {
        ...data,
        event_type: eventName,
        timestamp: Date.now(),
      },
    });
  }

  private async enqueueExperiment(channel: ExperimentBatchChannel, item: ExperimentBatchItem) {
    this.queues[channel].push(item);

    if (this.queues[channel].length >= this.batchSize) {
      await this.flush(channel);
      return;
    }

    this.scheduleFlush(channel);
  }

  private scheduleFlush(channel: ExperimentBatchChannel) {
    if (this.flushTimers[channel] || this.isQueueEmpty(channel)) {
      return;
    }

    this.flushTimers[channel] = setTimeout(() => {
      void this.flush(channel);
    }, this.batchTimeout);
  }

  private clearFlushTimer(channel: ExperimentBatchChannel) {
    if (this.flushTimers[channel]) {
      clearTimeout(this.flushTimers[channel]);
      this.flushTimers[channel] = null;
    }
  }

  private async flush(channel: ExperimentBatchChannel) {
    this.clearFlushTimer(channel);

    if (this.isQueueEmpty(channel)) {
      return;
    }

    try {
      const splitId = this.appEnvironment.getSplitId();

      if (channel === ExperimentBatchChannel.SPLIT_ID && isValidUuid(splitId)) {
        await this.httpClient.post('/split-tests/by-split-id', {
          data: {
            split_id: splitId,
            tests: this.queues[channel],
          },
        });

        this.clearQueue(channel);

        return;
      }

      await this.httpClient.post('/split-tests/by-user-id', {
        data: {
          tests: this.queues[channel],
        },
      });

      this.clearQueue(channel);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
    }
  }

  private clearQueue(channel: ExperimentBatchChannel) {
    this.queues[channel] = [];
  }

  private isQueueEmpty(channel: ExperimentBatchChannel) {
    return this.queues[channel].length === 0;
  }
}
